import type { DisplayCue } from "@expert/contentful/src/types";
import type { JSX } from "react";

import { Flex, Group, Stack, Text } from "@mantine/core";

import { useSalesContext } from "../../../../sales-context/SalesContext";
import { ListeningAnimation } from "../../../../shared-utils/common-ui/animations/listening";
import { useDetectedCuesStore } from "../../../../stores";
import { ChipRow } from "./ChipRow";
import classes from "./DetectedCues.module.css";
import { sortCues } from "./services/sortCues";

function getListeningText(cues: DisplayCue[], isInsightsEnabled: boolean, isChat: boolean): string {
    const hasPreviousDetections = cues.some((c) => c.isDetectedOnPreviousCall);
    const hasCurrentDetections = cues.some((c) => c.detected && !c.isDetectedOnPreviousCall);
    const interactionType = isChat ? "chat" : "call";

    if (isInsightsEnabled) return "Listening for tech and insights";
    if (hasPreviousDetections && hasCurrentDetections) return `Detected from previous and current ${interactionType}`;
    if (hasCurrentDetections) return `Detected from current ${interactionType}`;
    if (hasPreviousDetections) return `Detected from previous ${interactionType}`;
    return "Listening for devices";
}

export function DetectedCues(): JSX.Element | null {
    const { sessionId, features, taskType } = useSalesContext();
    const cues = useDetectedCuesStore((state) => state.getDisplayCues(sessionId));
    const hasSoftwareCues = !!cues.find((c) => c.type === "SOFTWARE");
    const hasDevicesCues = !!cues.find((c) => c.type === "DEVICE");
    const { isDigitalCompetencyInsightEnabled } = features;

    return (
        <Stack id="detected-devices-container" className={classes.text} gap="xs">
            {hasDevicesCues && (
                <>
                    <Stack gap="xs">
                        <Text size="0.75rem" fw="700" c="dark.2">
                            Devices
                        </Text>
                        <Group align="center" gap={4} wrap="nowrap">
                            <Text size="0.75rem" c="dark.2">
                                {getListeningText(cues, !!isDigitalCompetencyInsightEnabled, taskType.isChat)}
                            </Text>
                            {!isDigitalCompetencyInsightEnabled && <ListeningAnimation style={{ width: "1rem" }} />}
                        </Group>
                    </Stack>
                    <Flex className={classes.chipContainer} direction="row">
                        <ChipRow chips={sortCues(cues.filter((c) => c.type === "DEVICE"))} />
                    </Flex>
                </>
            )}
            {hasSoftwareCues && (
                <>
                    <Text size="0.75rem" fw="700" c="dark.2">
                        Software
                    </Text>
                    <Flex className={classes.chipContainer} direction="row">
                        <ChipRow chips={sortCues(cues.filter((c) => c.type === "SOFTWARE"))} />
                    </Flex>
                </>
            )}
        </Stack>
    );
}
